import {
  Box,
  Button,
  Group,
  Table as MantineTable,
  Menu,
  TableTrProps,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowRight, IconDots } from '@tabler/icons-react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router';
import useSortableData from 'src/common/components/table/hooks/useSortableData';
import { formatDateTime } from 'src/meetings/utils/date-utils';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { useFileDragDrop } from '../context/FileDragDropContext';
import { createDragElement } from '../utils/createDragElement';
import FileNameWithIcon from './FileNameWithIcon';
import classes from './FilesTable.module.css';

type TableData = UploadedFile & {
  isGenerated?: boolean;
};

type PrimaryActionType<T> = {
  label: string;
  variant?: 'primary' | 'secondary';
  icon?: React.ComponentType<{ size?: number | string }>;
  condition?: (entityWithFile: T) => boolean;
} & (
  | { type: 'link'; getLink: (entityWithFile: T) => string; onClick?: (entityWithFile: T) => void }
  | { type: 'action'; onClick: (entityWithFile: T) => void }
);

type Props<T extends TableData> = {
  entitiesWithFile: T[];
  isLoading: boolean;
  onClick?: (entityWithFile: T) => void;
  additionalColumns?: {
    label: string;
    valueBuilder: (f: T) => ReactElement;
  }[];
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
  enableDragDrop?: boolean;
};

type Action<T> = ActionItem<T> | Divider;

type Divider = {
  type: 'divider';
};

type ActionItem<T> = {
  label: string;
  action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown;
  condition?: (entityWithFile: T) => boolean;
};

function isDivider<T>(action: Action<T>): action is Divider {
  return 'type' in action && action.type === 'divider';
}

export default function FilesTable<T extends TableData>({
  entitiesWithFile,
  isLoading,
  primaryAction,
  additionalColumns = [],
  onClick = () => {},
  actions = [],
  enableDragDrop = false,
}: Props<T>) {
  const columns: DataTableColumn<T>[] = useMemo(
    () => [
      {
        accessor: 'file.fileName',
        title: 'Name',
        sortable: true,
        sortKey: 'file.fileName',
        render: record => (
          <FileNameWithIcon
            isFolder={Boolean(record.isFolder)}
            isGenerated={Boolean(record.isGenerated)}
            fileName={record.file.fileName}
            fileSize={record.file.fileSize}
          />
        ),
      },
      {
        accessor: 'file.createdAt',
        title: 'Date uploaded',
        sortable: true,
        sortKey: 'file.createdAt',
        render: record => (
          <Text fw={400} size="sm">
            {formatDateTime(record.file.createdAt)}
          </Text>
        ),
      },
      ...additionalColumns.map(col => ({
        accessor: col.label,
        title: col.label,
        render: (record: T) => col.valueBuilder(record),
      })),
      {
        accessor: 'actions',
        title: 'Actions',
        textAlign: 'right' as const,
        render: record => (
          <Group justify="space-between" wrap="nowrap">
            <PrimaryActionButton primaryAction={primaryAction} entityWithFile={record} />
            <ActionMenu entityWithFile={record} actions={actions} />
          </Group>
        ),
      },
    ],
    [additionalColumns, primaryAction, actions],
  );

  const { sortStatus, setSortStatus, sortedData } = useSortableData(entitiesWithFile, columns, {
    columnAccessor: 'file.createdAt',
    direction: 'desc',
  });

  const dragDropContext = useFileDragDrop();

  const rowFactory = useCallback(
    ({
      record,
      children,
      rowProps,
    }: {
      record: T;
      index: number;
      children: React.ReactNode;
      rowProps: TableTrProps;
      expandedElement?: React.ReactNode;
    }) => {
      const handleRowClick = (e: React.MouseEvent) => {
        if (dragDropContext?.draggedItem) {
          e.preventDefault();
          return;
        }
        onClick(record);
      };

      const handleDragStart = (e: React.DragEvent) => {
        if (dragDropContext) {
          e.dataTransfer.effectAllowed = 'move';
          dragDropContext.setDraggedItem(record);
          createDragElement(record, e);
        }
      };

      const handleDragEnd = () => {
        if (dragDropContext) {
          dragDropContext.setDraggedItem(null);
          dragDropContext.setIsDraggedOver(null);
        }
      };

      const handleDragOver = (e: React.DragEvent) => {
        if (dragDropContext && record.isFolder && dragDropContext.canDropOnTarget(record.id)) {
          e.preventDefault();
          dragDropContext.setIsDraggedOver(record.id);
        }
      };

      const handleDragLeave = () => {
        if (dragDropContext) {
          dragDropContext.setIsDraggedOver(null);
        }
      };

      const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        if (dragDropContext && record.isFolder && dragDropContext.draggedItem) {
          try {
            await dragDropContext.handleMoveFile(dragDropContext.draggedItem, record.id);
          } finally {
            dragDropContext.setDraggedItem(null);
            dragDropContext.setIsDraggedOver(null);
          }
        }
      };

      const isDragging = dragDropContext?.isDragging(record.id);
      const isDropTarget = dragDropContext?.isDropTarget(record.id);
      const draggable = enableDragDrop && !!dragDropContext;

      let className = rowProps.className || '';
      if (record.isFolder) {
        className += ` ${classes.folderRow}`;
      }
      if (draggable) {
        className += ` ${classes.draggableRow}`;
        if (isDragging) {
          className += ` ${classes.dragging}`;
        }
        if (isDropTarget && record.isFolder) {
          className += ` ${classes.dropTarget}`;
        }
      }

      return (
        <MantineTable.Tr
          {...rowProps}
          className={className.trim()}
          onClick={handleRowClick}
          draggable={draggable}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {children}
        </MantineTable.Tr>
      );
    },
    [dragDropContext, onClick, enableDragDrop],
  );

  const dataTableProps = {
    variant: 'bare' as const,
    columns,
    records: sortedData,
    fetching: isLoading,
    sortStatus,
    onSortStatusChange: setSortStatus,
    verticalSpacing: 'sm' as const,
    minHeight: isLoading || sortedData.length === 0 ? 200 : undefined,
    ...(enableDragDrop ? { rowFactory } : {}),
  };

  return (
    <Box>
      <DataTable {...dataTableProps} />
    </Box>
  );
}

type PrimaryActionButtonProps<T extends TableData> = {
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  entityWithFile: T;
};

const PrimaryActionButton = <T extends TableData>({
  primaryAction,
  entityWithFile,
}: PrimaryActionButtonProps<T>) => {
  const resolvedPrimaryAction =
    typeof primaryAction === 'function' ? primaryAction(entityWithFile) : primaryAction;

  const handleActionClick = useCallback(() => {
    if (resolvedPrimaryAction?.type === 'action') {
      resolvedPrimaryAction.onClick(entityWithFile);
    }
  }, [resolvedPrimaryAction, entityWithFile]);

  if (!resolvedPrimaryAction) {
    return <Box />;
  }

  const isDisabled =
    resolvedPrimaryAction.condition && !resolvedPrimaryAction.condition(entityWithFile);
  const variant = resolvedPrimaryAction.variant || 'primary';
  const IconComponent = resolvedPrimaryAction.icon || IconArrowRight;

  if (resolvedPrimaryAction.type === 'link') {
    return (
      <Button
        variant={variant}
        component={NavLink}
        disabled={isDisabled}
        onClick={() => {
          if (resolvedPrimaryAction.onClick) {
            resolvedPrimaryAction.onClick(entityWithFile);
          }
        }}
        to={resolvedPrimaryAction.getLink(entityWithFile)}
        rightSection={<IconComponent size={16} />}
      >
        {resolvedPrimaryAction.label}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      disabled={isDisabled}
      onClick={e => {
        e.stopPropagation();
        handleActionClick();
      }}
      rightSection={<IconComponent size={16} />}
    >
      {resolvedPrimaryAction.label}
    </Button>
  );
};

type ActionMenuProps<T extends TableData> = {
  entityWithFile: T;
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
};

const ActionMenu = <T extends TableData>({ entityWithFile, actions }: ActionMenuProps<T>) => {
  const resolvedActions = typeof actions === 'function' ? actions(entityWithFile) : actions;

  const handleMenuIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleMenuItemClick = useCallback(
    (action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown) => {
      return () => {
        const a = document.createElement('a');
        action(entityWithFile, a);
      };
    },
    [entityWithFile],
  );

  return (
    <Menu
      transitionProps={{ transition: 'pop-top-right' }}
      position="bottom-end"
      width={220}
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton data-testid="doc-three-dots" onClick={handleMenuIconClick}>
          <IconDots color="#475467" stroke={2} size={20} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown data-testid="doc-actions-menu">
        {resolvedActions?.map((a, i) => {
          if (isDivider(a)) {
            return <Menu.Divider key={i} />;
          }
          if (a.condition && !a.condition(entityWithFile)) {
            return null;
          }
          return (
            <Menu.Item
              key={i}
              onClick={e => {
                e.stopPropagation();
                handleMenuItemClick(a.action)();
              }}
            >
              {a.label}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
